import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { setCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { listingImage } from '../../config/configLayout';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { userCart, getUpdatedCart } from './cartUtils';

const RESULT_PAGE_SIZE = 100;

const defaultUserQueryParams = {
  expand: true,
  include: ['profileImage'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

const updateProfileQueryFn = async (arg, queryApi) => {
  const { dispatch, extra: sdk } = queryApi;
  const updateValues = { ...arg };
  const updateProfileResponse = await sdk.currentUser.updateProfile(
    updateValues,
    defaultUserQueryParams
  );
  const entities = denormalisedResponseEntities(updateProfileResponse);
  if (entities.length !== 1) {
    throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
  }
  const currentUser = entities[0];
  dispatch(setCurrentUser(currentUser));
  return { data: currentUser };
};

const updateCartQueryFn = (arg, queryApi, _extraOptions, _baseQuery) => {
  const { listing, quantity, deliveryMethod, action, currentUser } = arg;
  const cart = getUpdatedCart({ currentUser, listing, quantity, deliveryMethod, action });
  return updateProfileQueryFn({ publicData: { cart } }, queryApi, _extraOptions, _baseQuery);
};

const { aspectRatio: aspectRatioRaw, variantPrefix = 'listing-card' } = listingImage;
const aspectRatio = 1 / eval(aspectRatioRaw); // eslint-disable-line no-eval
const defaultListingParams = {
  include: ['author', 'author.profileImage', 'images', 'currentStock'],
  'fields.image': [
    'variants.scaled-small',
    'variants.scaled-medium',
    'variants.scaled-large',
    'variants.scaled-xlarge',
    `variants.${variantPrefix}`,
    `variants.${variantPrefix}-2x`,
    `variants.${variantPrefix}-4x`,
    `variants.${variantPrefix}-6x`,
    'variants.facebook',
    'variants.twitter',
    'variants.square-small',
    'variants.square-small2x',
  ],
  ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
};

const fetchCartListingsQueryFn = async (arg, queryApi) => {
  const { dispatch, extra: sdk } = queryApi;
  const { currentUser } = arg;
  const cart = userCart(currentUser);
  const ids = cart.map(i => i.id);
  if (ids.length === 0) return { data: [] };
  const queryParams = {
    ids,
    page: 1,
    perPage: RESULT_PAGE_SIZE,
    'limit.images': 1,
    ...defaultListingParams,
  };
  const response = await sdk.listings.query(queryParams);
  dispatch(addMarketplaceEntities(response));
  const data = denormalisedResponseEntities(response);
  return { data };
};

export const CartApi = createApi({
  reducerPath: 'CartApi',
  tagTypes: ['Cart', 'CartListings'],
  baseQuery: fakeBaseQuery(),
  endpoints: builder => ({
    updateCart: builder.mutation({
      queryFn: updateCartQueryFn,
      invalidatesTags: ['Cart'],
    }),
    fetchCartListings: builder.query({
      queryFn: fetchCartListingsQueryFn,
      invalidatesTags: ['CartListings'],
    }),
  }),
});

export const { useUpdateCartMutation, useFetchCartListingsQuery } = CartApi;
